import cx from "classnames";
import { faAngleRight } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";
import { PRIMARY_START_SMS_KEYWORD } from "common/constants/inbox";
import { toPhone } from "common/utils/formatting";

import Button from "client/common/components/buttons/Button";
import TextButton from "client/common/components/buttons/TextButton";
import ErrorMessage from "client/common/components/errors/ErrorMessage";
import {
  useAccountUpdateCommunicationPreferencesDrawer,
  useAccountUpdateEmailDrawer,
  useAccountUpdatePhoneDrawer,
} from "client/accounts/stackables";
import { useClinic } from "client/clinics/hooks";

import { DeliveryMethod } from "../types";
import { DELIVERY_METHOD_OPTIONS } from "../constants";
import { useDeliveryMethodSelection } from "../hooks";

import styles from "../styles/DeliveryMethodCard.module.less";

type DeliveryMethodCardProps = {
  value: Record<DeliveryMethod, boolean>;
  onChange: (value: Record<DeliveryMethod, boolean>) => void;
  errors: string[];
  disabled?: boolean;
};

const DeliveryMethodCard = ({
  value,
  onChange,
  errors,
  disabled = false,
}: DeliveryMethodCardProps) => {
  const clinic = useClinic();
  const { open: openUpdatePhone } = useAccountUpdatePhoneDrawer();
  const { open: openUpdateEmail } = useAccountUpdateEmailDrawer();
  const { open: openUpdateCommunicationPreferences } =
    useAccountUpdateCommunicationPreferencesDrawer();
  const {
    clientUser,
    destinations,
    availableMethods,
    isTextUnsubscribed,
    isTextHardUnsubscribed,
    availableMethodCount,
    isSelected,
  } = useDeliveryMethodSelection(value);

  const handleToggle = (method: DeliveryMethod) => {
    if (availableMethodCount > 1) {
      onChange({ ...value, [method]: !value[method] });
    }
  };

  const textDestination = isSelected("text") && destinations.text;
  const emailDestination = isSelected("email") && destinations.email;

  const renderDestinationSummary = () => {
    if (!textDestination) {
      return (
        <>
          Email to <strong>{emailDestination}</strong>
        </>
      );
    }

    return (
      <>
        Text to <strong>{textDestination}</strong>
        {emailDestination && (
          <>
            {" "}
            and email to <strong>{emailDestination}</strong>
          </>
        )}
      </>
    );
  };

  const missingDestinationHints = {
    text: {
      prompt: "To receive text reminders,",
      actionLabel: "add phone number",
      onClick: () => openUpdatePhone({}),
    },
    email: {
      prompt: "To get email reminders,",
      actionLabel: "add email address",
      onClick: () => openUpdateEmail({}),
    },
  };

  const missingMethod =
    clientUser && DELIVERY_METHOD_OPTIONS.find((option) => !destinations[option.value])?.value;
  const missingDestinationHint = missingMethod && missingDestinationHints[missingMethod];

  const renderTextUnsubscribedHint = () => {
    if (!isTextHardUnsubscribed) {
      return (
        <>
          You&apos;ve opted out of text updates.{" "}
          <TextButton
            type="button"
            hasUnderline={true}
            icon={faAngleRight}
            className={styles.hintAction}
            disabled={disabled}
            onClick={() => openUpdateCommunicationPreferences({})}
          >
            Update preferences
          </TextButton>
        </>
      );
    }

    if (!clinic.communication_number) {
      return <>Your clinic doesn&apos;t support text notifications.</>;
    }

    return (
      <>
        To enable text notifications, text <strong>{PRIMARY_START_SMS_KEYWORD}</strong> to{" "}
        <TextButton
          href={`sms:${clinic.communication_number.number}`}
          hasUnderline={true}
          className={styles.hintAction}
        >
          {toPhone(clinic.communication_number.number)}
        </TextButton>
        , then reload this page.
      </>
    );
  };

  const hasDestinationDetails = !!(
    textDestination ||
    emailDestination ||
    missingDestinationHint ||
    isTextUnsubscribed
  );

  return (
    <div
      className={cx(
        styles.deliveryMethodField,
        errors.length > 0 && styles.error,
        disabled && styles.disabled,
      )}
    >
      <span className={styles.label}>Deliver by</span>
      <div className={styles.options}>
        {DELIVERY_METHOD_OPTIONS.map((option) => (
          <Button
            key={option.value}
            className={styles.optionButton}
            size="s"
            aria-pressed={isSelected(option.value)}
            variant={isSelected(option.value) ? "primary" : "secondary"}
            disabled={disabled || !availableMethods[option.value]}
            onClick={() => handleToggle(option.value)}
          >
            <Icon icon={option.icon} />
            {option.label}
          </Button>
        ))}
      </div>
      {hasDestinationDetails && (
        <div className={styles.destinationDetails}>
          {(textDestination || emailDestination) && (
            <span className={styles.destination}>{renderDestinationSummary()}</span>
          )}
          {isTextUnsubscribed && (
            <span className={styles.hint}>{renderTextUnsubscribedHint()}</span>
          )}
          {missingDestinationHint && (
            <span className={styles.hint}>
              {missingDestinationHint.prompt}{" "}
              <TextButton
                type="button"
                hasUnderline={true}
                icon={faAngleRight}
                className={styles.hintAction}
                disabled={disabled}
                onClick={missingDestinationHint.onClick}
              >
                {missingDestinationHint.actionLabel}
              </TextButton>
            </span>
          )}
          {missingMethod === "email" && (
            <span className={styles.recommendation}>
              Adding an email is <strong>recommended</strong> as a backup
            </span>
          )}
        </div>
      )}
      {errors.map((error) => (
        <ErrorMessage key={error}>{error}</ErrorMessage>
      ))}
    </div>
  );
};

export default DeliveryMethodCard;
